import * as React from "react";

import { Input } from "@neworbit/simpleui-input";
import { Moment } from "moment";

import { IdentityValidationOptions } from "@teamprevent/common/immunisation/models";

import { immunisationTypesAndOutcomes, ImmunisationCreateModel } from "../model";

interface Props {
    immunisation: ImmunisationCreateModel;
    showErrors: boolean;
    updateProperty<
        TKey extends keyof ImmunisationCreateModel = keyof ImmunisationCreateModel,
        TValue = ImmunisationCreateModel[TKey]
    >(prop: TKey, value: TValue, valid?: boolean): void;
}

const completedByOptions = [
    { text: "Previous OH Provider", value: "Previous OH Provider" },
    { text: "GP Records", value: "GP Records" },
    { text: "Previous Optima Health Records", value: "Previous Optima Health Records" },
];

const immunisationTypes = (Object.keys(immunisationTypesAndOutcomes) as (keyof typeof immunisationTypesAndOutcomes)[]).map(t => ({ text: t, value: t }));

export const ImmunisationFields: React.FunctionComponent<Props> = props => {
    const immunisationTypeTypes = React.useMemo(
        () =>
            immunisationTypesAndOutcomes[props.immunisation.immunisationTypeName]
            && Object.keys(immunisationTypesAndOutcomes[props.immunisation.immunisationTypeName])?.map(t => ({ text: t, value: t })),
        [props.immunisation.immunisationTypeName]
    );
    const outcomes = React.useMemo(
        () => immunisationTypesAndOutcomes[props.immunisation.immunisationTypeName]?.[props.immunisation.recordType]?.map(t => ({ text: t, value: t })),
        [props.immunisation.immunisationTypeName, props.immunisation.recordType]
    );
    const onImmunisationTypeChanged = (immunisationTypeName: string, isValid: boolean) => {
        props.updateProperty("immunisationTypeName", immunisationTypeName, isValid);
        props.updateProperty("recordType", "");
        props.updateProperty("outcome", "");
    };

    const onCompletedDateChanged = (dateCompleted: Moment, isValid: boolean) =>
        props.updateProperty("dateCompleted", dateCompleted, isValid);

    const onOutcomeChanged = (outcome: string, isValid: boolean) =>
        props.updateProperty("outcome", outcome, isValid);

    const onNotesChanged = (notes: string, isValid: boolean) =>
        props.updateProperty("notes", notes, isValid);

    const onRecordTypeChanged = (recordType: string, isValid: boolean) => {
        props.updateProperty("recordType", recordType, isValid);
        props.updateProperty("outcome", "");
    };

    const onIdentityValidationChanged = (identityValidation: string, isValid: boolean) =>
        props.updateProperty("identityValidation", identityValidation === "true", isValid);

    const onCompletedByNameChanged = (completedByName: string, isValid: boolean) =>
        props.updateProperty("completedByName", completedByName, isValid);

    return (<>
        <Input.Dropdown
            label="Immunisation Type"
            value={props.immunisation.immunisationTypeName}
            options={immunisationTypes}
            onChange={onImmunisationTypeChanged}
            required
            showErrors={props.showErrors}
        />
        <Input.Date
            label="Date completed"
            value={props.immunisation.dateCompleted}
            onChange={onCompletedDateChanged}
            required
            showErrors={props.showErrors}
        />
        {props.immunisation.immunisationTypeName === "Other"
            ? <Input.Text
                value={props.immunisation.recordType}
                label="Type"
                showErrors={props.showErrors}
                required
                onChange={onRecordTypeChanged}
            />
            : <Input.Dropdown
                value={props.immunisation.recordType}
                label="Type"
                showErrors={props.showErrors}
                required
                onChange={onRecordTypeChanged}
                options={immunisationTypeTypes ?? []}
                dynamicOptions
            />}
        {props.immunisation.immunisationTypeName === "Other"
            ? <Input.Text
                label="Outcome"
                value={props.immunisation.outcome}
                onChange={onOutcomeChanged}
                required
                showErrors={props.showErrors}
            />
            : <Input.Dropdown
                label="Outcome"
                search
                value={props.immunisation.outcome}
                onChange={onOutcomeChanged}
                options={outcomes ?? []}
                required
                showErrors={props.showErrors}
                dynamicOptions
            />}
        <Input.Dropdown
            value={props.immunisation.identityValidation?.toString()}
            label="IVS Sample"
            options={IdentityValidationOptions.map(c => ({ text: c.text, value: c.value }))}
            showErrors={props.showErrors}
            required
            onChange={onIdentityValidationChanged}
        />
        <Input.Dropdown
            value={props.immunisation.completedByName}
            label="Completed by"
            showErrors={props.showErrors}
            required
            onChange={onCompletedByNameChanged}
            options={completedByOptions}
        />
        <Input.Text
            label="Notes"
            value={props.immunisation.notes}
            onChange={onNotesChanged}
            showErrors={props.showErrors}
        />
    </>);
};
